import { Box, Tooltip } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { PropsWithChildren, PropsWithClassName } from 'lib-shared-types-utils';
import { memo } from 'react';

import {
  CustomField,
  GetCustomFieldSelectFieldDisplayValueConfig,
  getCustomSelectFieldDisplayValue,
} from 'utils/customFields';

import styles from './customFieldDisplayValue.module.scss';

type CustomFieldDisplayValueProps<
  TCustomField extends CustomField = CustomField,
> = {
  /** The custom field. */
  customField: TCustomField;
  /** The configuration for displaying the custom field value. */
  config?: GetCustomFieldSelectFieldDisplayValueConfig;
} & PropsWithClassName;

/**
 * Renders the display value of a custom field attribute.
 *
 * For text attributes, the value is rendered as is. For hierarchical select
 * attributes, the attribute options are joined in a path-like string, with the
 * hierarchy represented by a separator, e.g. "One / Two / ... / Ten". When the
 * path is truncated, a tooltip is shown with the full path.
 */
export const CustomFieldDisplayValue = memo(function CustomFieldDisplayValue<
  TCustomField extends CustomField = CustomField,
>({
  className,
  customField,
  config,
}: CustomFieldDisplayValueProps<TCustomField>): JSX.Element | null {
  const { attribute, value } = customField;

  if (value?.data_type === 'string') {
    return <Value className={className}>{value.value}</Value>;
  }

  if ('options' in attribute && value?.data_type === 'string_list') {
    const displayValue = getCustomSelectFieldDisplayValue(
      { attribute, value },
      {
        nodeClassName: styles.subdued,
        separatorClassName: styles.subdued,
        ...config,
      },
    );

    if (!displayValue) return null;

    return (
      <Tooltip
        wrapProps={{
          className: styles.tooltipWrapper,
        }}
        content={displayValue.rawPath}
        disabled={!displayValue.isLongPath}
      >
        <Value className={className}>{displayValue.path}</Value>
      </Tooltip>
    );
  }

  return null;
});

const Value = ({
  children,
  className,
}: PropsWithChildren & PropsWithClassName): JSX.Element => {
  return (
    <Box className={classNames(styles.valueContainer, className)}>
      <Box as="span" className={styles.value}>
        {children}
      </Box>
    </Box>
  );
};
